import { createApi } from '@reduxjs/toolkit/query/react';
import { storeSavedSearch } from './helpers/saved-searches-storage';
import { sortSavedSearchesResponseOnLatestTotalCount } from './helpers/search-mapper';
import qs from 'qs';
import {
    type BlueshiftRecommendations4ApiResponse,
    type GetFavouriteSellersAndItems2ApiResponse,
    type ExecuteSavedSearchApiResponse,
    type ExecuteSavedSearchForCurrentUserApiRequest,
    type GetSavedSearchesApiResponse,
    type FavouritesellersAndItems2Request,
    type RecommendationsV4Request,
    type ItemsByIdsApiResponse,
    type ItemsItemsByIdsRequest,
    type IndependentSearchApiRequest,
    type IndependentSearchApiResponse,
    type CategoryPageApiRequest,
    type CategoryPageApiResponse,
    type BrandSuggestionsApiResponse,
    type IdeasApiResponse,
    type TrendingSearchesApiResponse,
    type TrendingSearchViewModel,
    SearchSortOrderApiDto,
    type CausePageApiRequest,
    type CausePageApiResponse,
    type StartPageSellersOfInterestRequest,
    type SellersOfInterestApiResponse,
    type StartPageIdeasRequest,
    type StartPageTrendingSearchesRequest
} from './types/webapi-discover-generated';
import { isServer } from 'tradera/utils/nextjs';
import { isomorphicBaseQueryForWishListableItems } from './utils/webapi-base-query';

export type RecommendationsQueryParams = {
    slot: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
    shippingCountryCode: string;
    maxResults: number;
    pageNumber: number;
};

export type SuggestedRecommendationsQueryParams = {
    slot: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
    shippingCountryCode: string;
    maxResults?: number;
    pageNumber?: number;
};

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getRecommendationsQueryParams = ({
    slot,
    userLanguage,
    automaticTranslationPreferred,
    shippingCountryCode,
    maxResults = 36,
    pageNumber = 1
}: SuggestedRecommendationsQueryParams): RecommendationsQueryParams => ({
    slot,
    maxResults,
    userLanguage: userLanguage.toLowerCase(),
    automaticTranslationPreferred,
    shippingCountryCode: shippingCountryCode.toUpperCase(),
    pageNumber
});

type FavouriteSellersQueryParams = {
    shippingCountryCode: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
};

export type LastChanceQueryParams = {
    automaticTranslationPreferred: boolean;
    itemCount: number;
    shippingCountryCode: string;
    userLanguage: string;
};

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getFavouriteSellersQueryParams = ({
    shippingCountryCode,
    userLanguage,
    automaticTranslationPreferred
}: FavouriteSellersQueryParams): FavouriteSellersQueryParams => ({
    shippingCountryCode: shippingCountryCode.toUpperCase(),
    userLanguage: userLanguage.toLowerCase(),
    automaticTranslationPreferred
});

enum SavedSearchesSorting {
    Sorted = 'sorted',
    Unsorted = 'unsorted'
}

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching.
 */
export const getSavedSearchesQueryParams = (isSortingRequired: boolean) =>
    isSortingRequired
        ? SavedSearchesSorting.Sorted
        : SavedSearchesSorting.Unsorted;

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getInstagramQueryParams = () => ({});

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getLastSearchQueryParams = (
    queryParams: Record<string, unknown> | undefined
) => {
    const modifiedParams = {
        ...queryParams,
        // We always want to sort on newest added to get a more "alive" feeling on the start page.
        // And it would be confusing for the user if they clicked the link to get to all search
        // results if they didn't see the same items on top as they did on the start page.
        sortBy: SearchSortOrderApiDto.AddedOn
    };
    return modifiedParams;
};

export const getRecentlyViewedQueryParams = (
    languageCode: string,
    automaticTranslationPreferred: boolean,
    itemIds: number[]
) => ({
    searchLanguageCodeIso2: languageCode,
    automaticTranslationPreferred,
    itemIds
});

export const discoverApi = createApi({
    reducerPath: 'discoveryApi',
    baseQuery: isomorphicBaseQueryForWishListableItems,
    endpoints: (builder) => ({
        getBrandSuggestions: builder.query<
            BrandSuggestionsApiResponse,
            unknown
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/brand-suggestions`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getFavouriteSellers: builder.query<
            GetFavouriteSellersAndItems2ApiResponse,
            FavouritesellersAndItems2Request
        >({
            query: ({
                shippingCountryCode,
                userLanguage,
                automaticTranslationPreferred,
                maxItemsPerFavouriteSeller = 3,
                maxItems = 20,
                maxFavouriteSellers = 20
            }) => {
                const query = qs.stringify({
                    shippingCountryCode,
                    userLanguage,
                    automaticTranslationPreferred,
                    maxItemsPerFavouriteSeller,
                    maxItems,
                    maxFavouriteSellers
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/favouritesellers-and-items2?${query}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getSellersOfInterest: builder.query<
            SellersOfInterestApiResponse,
            StartPageSellersOfInterestRequest
        >({
            query: ({ shippingCountryCode, maxSellers = 20 }) => {
                const query = qs.stringify({
                    shippingCountryCode,
                    maxSellers
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/sellers-of-interest?${query}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getIdeas: builder.query<IdeasApiResponse, StartPageIdeasRequest>({
            query: ({ languageCodeIso2 }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/ideas?languageCodeIso2=${languageCodeIso2}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getInstagramFeed: builder.query<
            GetFavouriteSellersAndItems2ApiResponse,
            FavouritesellersAndItems2Request
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/instagram-feed`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getTrendingSearches: builder.query<
            TrendingSearchViewModel[],
            StartPageTrendingSearchesRequest
        >({
            query: ({ languageCodeIso2 }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/trending-searches?languageCodeIso2=${languageCodeIso2}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            },
            transformResponse: (response: TrendingSearchesApiResponse) => {
                return response.searches || [];
            }
        }),
        getSavedSearches: builder.query<GetSavedSearchesApiResponse, unknown>({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/savedsearches`;
            },
            extraOptions: {
                getWishListableItems: () => null
            },
            transformResponse: (response, _meta, arg) => {
                return arg === SavedSearchesSorting.Unsorted
                    ? response
                    : sortSavedSearchesResponseOnLatestTotalCount(response);
            }
        }),
        getRecommendations: builder.query<
            BlueshiftRecommendations4ApiResponse,
            RecommendationsV4Request
        >({
            query: ({
                slot,
                shippingCountryCode,
                userLanguage,
                automaticTranslationPreferred,
                maxResults,
                pageNumber
            }: RecommendationsQueryParams) => {
                const query = qs.stringify({
                    slot,
                    pageNumber,
                    shippingCountryCode,
                    userLanguage,
                    automaticTranslationPreferred,
                    maxResults
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/recommendations-v4?${query}`;
            },
            extraOptions: {
                getWishListableItems: (
                    response: BlueshiftRecommendations4ApiResponse
                ) => response.items
            }
        }),
        getIndependentSearchResult: builder.query<
            IndependentSearchApiResponse,
            IndependentSearchApiRequest
        >({
            query: (parameters) => {
                const baseUrl = isServer ? '' : '/api/webapi';

                return {
                    url: `${baseUrl}/discover/web/independent-search`,
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                getWishListableItems: (
                    response: IndependentSearchApiResponse
                ) => response.items
            }
        }),

        getCategoryResult: builder.query<
            CategoryPageApiResponse,
            CategoryPageApiRequest
        >({
            query: (parameters) => {
                return {
                    url: '/api/webapi/discover/web/category-page',
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                getWishListableItems: (response: CategoryPageApiResponse) =>
                    response.items
            }
        }),
        getSavedSearchResults: builder.mutation<
            ExecuteSavedSearchApiResponse,
            ExecuteSavedSearchForCurrentUserApiRequest
        >({
            query: (parameters) => {
                return {
                    url: '/api/webapi/discover/savedsearches/search-v2',
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                //response.response?.result?.items are of type SearchResultItem2ApiDto which does not currently have the "inWishList" property
                getWishListableItems: () => null
            },
            transformResponse: (
                response: ExecuteSavedSearchApiResponse,
                meta,
                arg
            ) => {
                storeSavedSearch(arg.id, {
                    totalItems: response.response?.result?.itemsMatched
                });
                return response;
            }
        }),
        getItemsByIds: builder.query<
            ItemsByIdsApiResponse,
            ItemsItemsByIdsRequest
        >({
            query: (params) => {
                const baseUrl = isServer ? '' : '/api/webapi';

                const query = qs.stringify(params);
                return `${baseUrl}/discover/web/items/items-by-ids?${query}`;
            },
            extraOptions: {
                getWishListableItems: (response: ItemsByIdsApiResponse) =>
                    response.items
            }
        }),
        getCausePage: builder.query<CausePageApiResponse, CausePageApiRequest>({
            query: (params) => {
                return {
                    url: '/api/webapi/discover/web/custom-result-pages/cause-page',
                    method: 'POST',
                    body: params
                };
            },
            extraOptions: {
                getWishListableItems: (response: ItemsByIdsApiResponse) =>
                    response.items
            }
        })
    })
});

// KATS: break this up into more files, do not append more endpoints to this file,
// we have decided that it's an anti-pattern to have a single file with all endpoints.
// https://traderasweden.slack.com/archives/C01BUB6G7UJ/p1699018076088839?thread_ts=1698937051.165139&cid=C01BUB6G7UJ
export const {
    useGetBrandSuggestionsQuery,
    useGetIdeasQuery,
    useGetSellersOfInterestQuery,
    useGetInstagramFeedQuery,
    useGetIndependentSearchResultQuery,
    useGetCategoryResultQuery,
    useGetSavedSearchesQuery,
    useGetSavedSearchResultsMutation,
    useGetRecommendationsQuery,
    useGetFavouriteSellersQuery,
    useGetTrendingSearchesQuery,
    useGetItemsByIdsQuery,
    useGetCausePageQuery,
    endpoints
} = discoverApi;

export const { updateQueryData } = discoverApi.util;
